import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Snackbar,
  alpha,
  useTheme,
  Divider,
} from '@mui/material';
import { useMutation, useQueryClient } from 'react-query';
import { createHoliday } from '../../../api/services/Candidate/holiday';
import CustomSwitch from '../../FormFields/CustomSwitch';
import CustomDateField from '../../FormFields/CustomDateField';
import CustomTextArea from '../../CustomForm/CustomTextArea';
import CustomInput from '../../FormFields/CustomInput';
import { BeachAccess as BeachIcon, Send as SendIcon } from '@mui/icons-material';

const QK = {
  holidays: 'holidays',
  holidaySummary: 'holidaySummary',
  holidayBalance: 'holidayBalance',
};

export default function HolidayRequest({ candidateId, selectedYear, balanceData, onSuccess }) {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [halfDayFrom, setHalfDayFrom] = useState(false);
  const [halfDayTo, setHalfDayTo] = useState(false);
  const [duration, setDuration] = useState(0);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const currentBalance = balanceData || {
    remaining: 28.00,
  };

  const createMutation = useMutation(createHoliday, {
    onSuccess: () => {
      queryClient.invalidateQueries([QK.holidays, candidateId]);
      queryClient.invalidateQueries([QK.holidaySummary, candidateId]);
      queryClient.invalidateQueries([QK.holidayBalance, candidateId]);
      setSnackbarMessage(`Holiday request submitted for ${duration.toFixed(2)} days`);
      setSnackbarOpen(true);
      setDateFrom('');
      setDateTo('');
      setHalfDayFrom(false);
      setHalfDayTo(false);
      setNotes('');
      setDuration(0);
      if (onSuccess) onSuccess();
    },
    onError: (error) => {
      setSnackbarMessage(error.response?.data?.message || 'Failed to submit holiday request');
      setSnackbarOpen(true);
    },
  });

  const calculateDuration = (from, to, halfFrom, halfTo) => {
    if (!from || !to) return 0;

    const start = new Date(from);
    const end = new Date(to);
    
    if (start > end) {
      setError('Date from cannot be after date to');
      return 0;
    }

    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

    let totalDays = diffDays;

    if (halfFrom) totalDays -= 0.5;
    if (halfTo) totalDays -= 0.5;

    if (start.getTime() === end.getTime()) {
      if (halfFrom && halfTo) return 0.5;
      if (halfFrom || halfTo) return 0.5;
      return 1;
    }

    if (diffDays === 1) {
      if (halfFrom && halfTo) return 0.5;
      if (halfFrom || halfTo) return 0.5;
      return 1;
    }

    return Math.max(totalDays, 0.5);
  };

  const handleDateFromChange = (e) => {
    setDateFrom(e.target.value);
    setError('');
  };

  const handleDateToChange = (e) => {
    setDateTo(e.target.value);
    setError('');
  };

  useEffect(() => {
    if (dateFrom && dateTo) {
      const calculated = calculateDuration(dateFrom, dateTo, halfDayFrom, halfDayTo);
      setDuration(calculated);
      setError('');

      if (calculated > currentBalance.remaining) {
        setError(`You have only ${currentBalance.remaining.toFixed(2)} days available. Requested: ${calculated.toFixed(2)} days`);
      }
    }
  }, [dateFrom, dateTo, halfDayFrom, halfDayTo, currentBalance.remaining]);

  const handleSubmit = () => {
    if (!dateFrom || !dateTo) {
      setSnackbarMessage('Please select date from and date to');
      setSnackbarOpen(true);
      return;
    }

    if (duration > currentBalance.remaining) {
      setSnackbarMessage(`Cannot request ${duration.toFixed(2)} days. Only ${currentBalance.remaining.toFixed(2)} days available.`);
      setSnackbarOpen(true);
      return;
    }

    if (duration <= 0) {
      setSnackbarMessage('Invalid duration. Please check your dates.');
      setSnackbarOpen(true);
      return;
    }

    const body = {
      candidate: candidateId,
      date_from: dateFrom,
      date_to: dateTo,
      half_day_from: halfDayFrom,
      half_day_to: halfDayTo,
      notes: notes,
    };

    createMutation.mutate(body);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '8px',
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <BeachIcon sx={{ color: theme.palette.primary.main, fontSize: 18 }} />
        </Box>
        <Typography variant="subtitle1" fontWeight={600} color="#1A1A2E">
          Request Holiday
        </Typography>
        <Typography variant="caption" color="#6B7280">
          Submit holiday request for {selectedYear}
        </Typography>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
          <CustomDateField
            label="Date From"
            name="date_from"
            value={dateFrom}
            onChange={handleDateFromChange}
            fullWidth
            size="small"
            format="DD/MM/YYYY"
          />
          <Box sx={{ mt: 0.5 }}>
            <CustomSwitch
              label="Half Day"
              checked={halfDayFrom}
              onChange={(e) => setHalfDayFrom(e.target.checked)}
              size="small"
            />
          </Box>
        </Box>
        <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
          <CustomDateField
            label="Date To"
            name="date_to"
            value={dateTo}
            onChange={handleDateToChange}
            fullWidth
            size="small"
            format="DD/MM/YYYY"
          />
          <Box sx={{ mt: 0.5 }}>
            <CustomSwitch
              label="Half Day"
              checked={halfDayTo}
              onChange={(e) => setHalfDayTo(e.target.checked)}
              size="small"
            />
          </Box>
        </Box>
        <Box sx={{ flex: '1 1 calc(33.33% - 8px)', minWidth: '150px' }}>
          <CustomInput
            label="Duration"
            fullWidth
            size="small"
            value={`${duration.toFixed(1)} days`}
            disabled
            sx={{
              '& .MuiInputBase-root': {
                bgcolor: '#F8FAFC',
                borderRadius: 2,
              },
            }}
          />
        </Box>
        <Box sx={{ flex: '1 1 calc(66.66% - 8px)', minWidth: '200px' }}>
          <CustomTextArea
            label="Notes"
            placeholder="Add notes..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={1}
            fullWidth
            size="small"
          />
        </Box>
        <Box sx={{ width: '100%', mt: 1 }}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={duration > currentBalance.remaining || duration <= 0 || createMutation.isLoading}
            startIcon={createMutation.isLoading ? null : <SendIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '8px',
              bgcolor: '#2469F0',
              px: 4,
              py: 1.2,
              width: '100%',
              '&:hover': {
                bgcolor: '#1A5BD4',
                boxShadow: '0px 4px 12px rgba(36, 105, 240, 0.3)',
              },
              '&.Mui-disabled': {
                bgcolor: '#F1F5F9',
                color: '#94A3B8',
              },
            }}
          >
            {createMutation.isLoading ? 'Submitting...' : 'Submit Request'}
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="success">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}